/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { prepScopes } from '../utils/GraphHelpers';
/**
 * Person Type enum
 *
 * @export
 * @enum {number}
 */
export var PersonType;
(function (PersonType) {
    /**
     * Any type
     */
    PersonType[PersonType["Any"] = 0] = "Any";
    /**
     * A Person such as User or Contact
     */
    PersonType["Person"] = "Person";
    /**
     * A group
     */
    PersonType["Group"] = "Group";
})(PersonType || (PersonType = {}));
/**
 * async promise, returns all Graph people who are most relevant contacts to the signed in user.
 *
 * @param {string} query
 * @param {number} [top=10] - number of people to return
 * @param {PersonType} [personType=PersonType.Person] - the type of person to search for
 * @returns {(Promise<Person[]>)}
 */
export function findPeople(graph, query, top = 10, personType = PersonType.Person) {
    return __awaiter(this, void 0, void 0, function* () {
        const scopes = 'people.read';
        let filterQuery = '';
        if (personType !== PersonType.Any) {
            filterQuery = `personType/class eq '${personType}'`;
        }
        const result = yield graph
            .api('/me/people')
            .search('"' + query + '"')
            .top(top)
            .filter(filterQuery)
            .middlewareOptions(prepScopes(scopes))
            .get();
        return result ? result.value : null;
    });
}
/**
 * async promise to the Graph for People, by default, it will request the most frequent contacts for the signed in user.
 *
 * @returns {(Promise<Person[]>)}
 * @memberof Graph
 */
export function getPeople(graph) {
    return __awaiter(this, void 0, void 0, function* () {
        const scopes = 'people.read';
        const uri = '/me/people';
        const people = yield graph
            .api(uri)
            .middlewareOptions(prepScopes(scopes))
            .filter("personType/class eq 'Person'")
            .get();
        return people ? people.value : null;
    });
}
/**
 * async promise to the Graph for People, defined by a group id
 *
 * @param {string} groupId
 * @returns {(Promise<Person[]>)}
 * @memberof Graph
 */
export function getPeopleFromGroup(graph, groupId) {
    return __awaiter(this, void 0, void 0, function* () {
        const scopes = 'people.read';
        const uri = `/groups/${groupId}/members`;
        const people = yield graph
            .api(uri)
            .middlewareOptions(prepScopes(scopes))
            .get();
        return people ? people.value : null;
    });
}
/**
 * returns a promise that resolves after specified time
 * @param time in milliseconds
 */
export function getEmailFromGraphEntity(entity) {
    const person = entity;
    const user = entity;
    const contact = entity;
    if (user.mail) {
        return user.mail;
    }
    else if (person.scoredEmailAddresses && person.scoredEmailAddresses.length) {
        return person.scoredEmailAddresses[0].address;
    }
    else if (contact.emailAddresses && contact.emailAddresses.length) {
        return contact.emailAddresses[0].address;
    }
    return null;
}
/**
 * async promise, returns a Graph contact associated with the email provided
 *
 * @param {string} email
 * @returns {(Promise<Contact[]>)}
 * @memberof Graph
 */
export function findContactByEmail(graph, email) {
    return __awaiter(this, void 0, void 0, function* () {
        const scopes = 'contacts.read';
        const result = yield graph
            .api('/me/contacts')
            .filter(`emailAddresses/any(a:a/address eq '${email}')`)
            .middlewareOptions(prepScopes(scopes))
            .get();
        return result ? result.value : null;
    });
}
/**
 * async promise, returns Graph contact and/or Person associated with the email provided
 * Uses: Graph.findPerson(email) and Graph.findContactByEmail(email)
 *
 * @param {string} email
 * @returns {(Promise<Array<Person | Contact>>)}
 * @memberof Graph
 */
export function findUserByEmail(graph, email) {
    return Promise.all([findPeople(graph, email), findContactByEmail(graph, email)]).then(([people, contacts]) => {
        return (people || []).concat(contacts || []);
    });
}
//# sourceMappingURL=graph.people.js.map